import { useEffect, useState, type CSSProperties } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { ContractStatus, type ContractSummaryDto } from '@bhops/shared';
import { contractsApi } from '@/api/contracts';
import { extractApiError } from '@/api/client';
import { kstYmd } from '@/lib/format-date';
import { useDebounce } from '@/hooks/useDebounce';
import { Button } from '@/components/Form';
import { Pagination } from '@/components/Pagination';
import { TableSkeleton } from '@/components/TableSkeleton';
import { useAuthStore } from '@/stores/auth';
import { canEditSales } from '@/permissions';

const STATUS_LABEL: Record<ContractStatus, string> = {
  DRAFT: '초안',
  ACTIVE: '활성',
  EXPIRED: '만료',
  TERMINATED: '해지',
};

const STATUS_COLOR: Record<ContractStatus, string> = {
  DRAFT: 'var(--fg-muted)',
  ACTIVE: 'var(--robot-operating)',
  EXPIRED: 'var(--fg-warning)',
  TERMINATED: 'var(--fg-danger)',
};

interface Props {
  /**
   * Plan v0.3.0 §F-3 — when true, render in a tighter shell suitable
   * for embedding inside CustomerMasterPage's sub-tab. Skips the
   * page header (h1) so the parent's customer header isn't doubled up.
   */
  embedded?: boolean;
  /**
   * §F-3 — when set, filter the list to this customer's contracts
   * only. Passed through to `contractsApi.list({ customerId })`.
   */
  customerId?: string;
}

export function ContractsPage({ embedded = false, customerId: customerIdProp }: Props = {}) {
  // 개선 9건 차수2e(26-08-04) ㉣ — URL 로도 고객사를 좁힌다.
  //
  // 고객사 상세의 계약 섹션이 이 화면으로 오는 길을 냈다(계약이 2건 이상인 고객사가
  // 늘어난다 — '추가 판매는 새 계약으로' 규칙). 링크에 customerId 를 달아도 이 화면이
  // 읽지 않으면 전체 계약 목록이 열려, 링크가 좁혀 준다고 **말만 하고** 안 좁히는 것이 된다.
  //
  // 정정(26-08-04 감사) — 차수2e 는 여기 주석에 "인프라 발주 목록과 같은 방식" 이라고
  // 적었지만 사실이 아니었다. 인프라 발주 목록은 파라미터를 읽는 것에 더해 '한 고객사만
  // 표시 중' 배너와 '전체 보기' 를 함께 띄운다. 차수2e 는 조회만 좁히고 화면에서는
  // 아무 말도 하지 않아, 좁힘이 보이지 않은 채 계약이 다시 숨었다. 아래에서 배너와
  // '전체 보기' 를 갖춰 그 관례를 실제로 따른다.
  //
  // prop 이 우선이다 — 임베드로 쓸 때 부모가 정한 고객사를 URL 이 뒤집으면 안 된다.
  const [searchParams, setSearchParams] = useSearchParams();
  // '?customerId=' (빈 값)은 좁힘이 아니다 — 빈 문자열이 조회·배너 조건으로
  // 새는 것을 막는다.
  const customerIdFromUrl = searchParams.get('customerId') || undefined;
  const customerId = customerIdProp || customerIdFromUrl;
  // 배너와 '전체 보기' 는 URL 로 좁혀 들어온 경우에만 낸다. prop 으로 좁힌 임베드는
  // 부모 화면이 이미 고객사를 머리글에 밝히고 있고, 파라미터를 지워도 prop 이 이기므로
  // '전체 보기' 가 아무 일도 못 하는 죽은 버튼이 된다.
  const narrowedByUrl = !customerIdProp && !!customerIdFromUrl;
  // round3 #8d — 백엔드 CASL(ability.factory)·ContractsService.create 는 SALES_MANAGER
  // 에게도 Contract create 를 허용한다. 게이트가 ADMIN/MANAGER 로만 좁아 영업팀장이
  // '+새 계약' 버튼을 못 보던 RBAC drift → canEditSales 로 정렬(진입점 게이트).
  const role = useAuthStore((s) => s.user?.role);
  const canCreateContract = role ? canEditSales(role) : false;
  const [filter, setFilter] = useState<{ status?: ContractStatus; search: string }>({
    search: '',
  });
  // docs/work_log — debounce search input so we don't hit the API on
  // every keystroke (300ms). Query key uses debouncedSearch.
  const debouncedSearch = useDebounce(filter.search, 300);
  // docs/work_log — page-size + page controlled by Pagination component.
  // Default 100 to match the historical pageSize for this page.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);

  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ['contracts', { ...filter, search: debouncedSearch }, customerId, page, pageSize],
    queryFn: () =>
      contractsApi.list({
        status: filter.status,
        search: debouncedSearch || undefined,
        ...(customerId ? { customerId } : {}),
        page,
        pageSize,
      }),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  // 배너에 쓸 고객사명 — 목록 응답 행의 customer 스냅샷에서 얻는다(별도 조회 없음.
  // 좁힘이 걸리면 모든 행이 같은 고객사이므로 첫 행으로 충분하다).
  // 응답에 customer 관계가 없거나 결과가 0건이면 이름을 알 수 없다 — 그때는
  // 지어내지 않고 '선택한 고객사' 로만 말한다.
  const nameFromRows = customerId ? (items[0]?.customer?.displayName ?? null) : null;
  // 한 번 본 이름은 같은 고객사 안에서 기억한다. 검색·상태를 얹어 결과가 0건이 되는
  // 순간이 배너가 가장 필요한 순간("검색해도 안 나온다")인데, 하필 그때 이름을
  // 읽을 행이 사라진다. 고객사가 바뀌면(키 불일치) 기억은 버린다 — 다른 고객사
  // 이름을 집어오면 안 된다.
  const [seenName, setSeenName] = useState<{ customerId: string; displayName: string } | null>(
    null,
  );
  useEffect(() => {
    if (!customerId || !nameFromRows) return;
    setSeenName((prev) =>
      prev && prev.customerId === customerId && prev.displayName === nameFromRows
        ? prev
        : { customerId, displayName: nameFromRows },
    );
  }, [customerId, nameFromRows]);
  const narrowedCustomerName =
    nameFromRows ??
    (seenName !== null && seenName.customerId === customerId ? seenName.displayName : null);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: embedded ? 8 : 16 }}>
      {/* §F-3 — page header hidden in embedded mode (parent shows customer). */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {!embedded && (
          <h1 className="kuun-h2" style={{ margin: 0 }}>
            계약
          </h1>
        )}
        <select
          value={filter.status ?? ''}
          onChange={(e) => {
            setFilter((f) => ({
              ...f,
              status: (e.target.value || undefined) as ContractStatus | undefined,
            }));
            setPage(1);
          }}
          style={selectStyle}
        >
          <option value="">전체 상태</option>
          {Object.values(ContractStatus).map((s) => (
            <option key={s} value={s}>
              {STATUS_LABEL[s]}
            </option>
          ))}
        </select>
        <input
          placeholder="고객사 이름이나 계약번호로 검색"
          value={filter.search}
          onChange={(e) => {
            setFilter((f) => ({ ...f, search: e.target.value }));
            setPage(1);
          }}
          style={searchInput}
        />
        {/* 좁힘이 걸리면 이 숫자는 전체 계약 수가 아니다 — 말을 바꿔 그것을 밝힌다.
            26-08-04 감사 확정 결함(P3) — 이 조회는 keepPreviousData 라, 좁힘을 켜거나
            끈 직후 표에는 **이전 응답**(다른 범위의 행)이 남아 있는데 문구는 즉시 새
            범위로 바뀌었다. '전체 보기' 를 누른 직후 한 고객사 행을 보여 주면서 '총 N 건'
            이라고 말한 구간이 그것이다. 다시 받아오는 동안에는 숫자를 말하지 않는다 —
            고객사 마스터(pages/business/CustomerMasterPage)가 같은 상황에서 쓰는 방식. */}
        <div className="kuun-caption" style={{ marginLeft: 'auto', color: 'var(--fg-muted)' }}>
          {isFetching
            ? '불러오는 중…'
            : customerId
              ? `이 고객사 계약 ${total.toLocaleString()} 건`
              : `총 ${total.toLocaleString()} 건`}
        </div>
        {/* §F-3 follow-up — propagate customerId so ContractCreateWizard
            can scope its eligible-Deals picker to the embedded customer. */}
        {canCreateContract && (
          <Link
            to={customerId ? `/contracts/new?customerId=${customerId}` : '/contracts/new'}
            style={newBtn}
          >
            + 새 계약
          </Link>
        )}
      </div>

      {/* 좁힘 배너 + '전체 보기' — 인프라 발주 목록(pages/InfrastructurePage)의 관례.
          좁혀 놓고 말하지 않으면 운영자는 "계약이 없어졌다"고 읽는다. */}
      {narrowedByUrl && (
        <div style={filterBanner}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span>
              <strong>{narrowedCustomerName ?? '선택한 고객사'}</strong>의 계약만 보고 있습니다
            </span>
            {/* ㉡ — 상태·검색이 이 좁힘 위에 함께 걸린다는 사실을 말한다. 이걸 말하지
                않으면 "검색해도 안 나온다"가 된다. */}
            <span className="kuun-caption" style={{ color: 'var(--fg-muted)' }}>
              상태와 검색도 이 고객사 안에서만 찾습니다. 다른 고객사 계약을 찾으려면 전체 보기로
              돌아가세요.
            </span>
          </div>
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              // customerId 파라미터만 제거(다른 파라미터는 보존). 전체 목록 복귀.
              const next = new URLSearchParams(searchParams);
              next.delete('customerId');
              setSearchParams(next, { replace: true });
            }}
          >
            전체 보기
          </Button>
        </div>
      )}

      <div style={tableWrap}>
        {isLoading && !data ? (
          <TableSkeleton rows={8} columns={8} />
        ) : error ? (
          <div style={empty}>
            <span style={{ color: 'var(--fg-danger)' }}>{extractApiError(error)}</span>
          </div>
        ) : items.length === 0 ? (
          <div style={empty} className="kuun-dot-grid">
            {/* 좁힘이 걸린 채 0건이면 "등록된 계약이 없습니다" 는 거짓말이 된다 —
                보고 있는 범위가 한 고객사(+상태·검색)라는 사실을 밝힌다. */}
            {customerId ? (
              <>
                <div className="kuun-h4">조건에 맞는 계약이 없습니다</div>
                <div className="kuun-caption" style={{ marginTop: 4 }}>
                  지금 한 고객사만 보고 있습니다.
                  {(filter.status || debouncedSearch) && ' 상태와 검색 조건도 함께 걸려 있습니다.'}
                </div>
              </>
            ) : (
              <>
                <div className="kuun-h4">등록된 계약이 없습니다</div>
                <div className="kuun-caption" style={{ marginTop: 4 }}>
                  계약은 협상 단계의 영업기회에서 만듭니다.
                </div>
              </>
            )}
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr>
                <th style={th}>계약번호</th>
                <th style={th}>고객사</th>
                <th style={th}>매장</th>
                <th style={th}>Deal</th>
                <th style={th}>상태</th>
                <th style={th}>시작일</th>
                <th style={th}>종료일</th>
                <th style={th}>구성</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <ContractRow key={c.id} contract={c} />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {total > 0 && (
        <Pagination
          page={page}
          pageSize={pageSize}
          total={total}
          pageSizeOptions={[50, 100, 200]}
          onPageChange={setPage}
          onPageSizeChange={(s) => {
            setPageSize(s);
            setPage(1);
          }}
        />
      )}
    </div>
  );
}

function ContractRow({ contract: c }: { contract: ContractSummaryDto }) {
  const navigate = useNavigate();
  const customerName = c.customer?.displayName ?? '-';
  const storeName = c.store ? [c.store.name, c.store.branchName].filter(Boolean).join(' · ') : '-';
  const dealRef = c.deal ? `${c.deal.humanId} (${c.deal.stage})` : c.dealId.slice(0, 8);
  return (
    <tr
      style={{ borderTop: '1px solid var(--border-subtle)', cursor: 'pointer' }}
      // round3 #8f — 행 클릭은 계약 상세로(복구). 목록은 전 계약을 보여주므로
      // 각 행에서 라인/가격/PDF/해지 상세에 도달 가능.
      onClick={() => navigate(`/contracts/${c.id}`)}
    >
      <td style={{ ...td, fontFamily: 'var(--font-mono)' }}>{c.humanId}</td>
      <td style={td}>{customerName}</td>
      <td style={td}>{storeName}</td>
      <td style={{ ...td, fontFamily: 'var(--font-mono)', fontSize: 11 }}>{dealRef}</td>
      <td style={td}>
        <span style={{ color: STATUS_COLOR[c.status], fontWeight: 500 }}>
          {STATUS_LABEL[c.status]}
        </span>
        {c.terminationReason && (
          <span className="kuun-caption" style={{ marginLeft: 6, color: 'var(--fg-muted)' }}>
            ({c.terminationReason})
          </span>
        )}
      </td>
      {/* 추가수정 2차(26-07-27) §2 — 계약일을 KST 로. toYmd(UTC 자르기)는 등록
          마법사가 한국 자정으로 저장한 계약일을 하루 이르게 보여줬다. */}
      <td style={{ ...td, fontFamily: 'var(--font-mono)' }}>{kstYmd(c.startDate) ?? '-'}</td>
      <td style={{ ...td, fontFamily: 'var(--font-mono)' }}>{kstYmd(c.endDate) ?? '-'}</td>
      <td style={{ ...td, color: 'var(--fg-muted)', fontSize: 11 }}>
        {c._count ? `${c._count.lines}라인 · ${c._count.pricings}가격` : '-'}
      </td>
    </tr>
  );
}

const selectStyle: CSSProperties = {
  marginLeft: 12,
  height: 32,
  padding: '0 8px',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-input)',
  background: 'var(--bg-surface)',
  fontSize: 13,
};

const searchInput: CSSProperties = {
  marginLeft: 6,
  width: 240,
  height: 32,
  padding: '0 10px',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-input)',
  background: 'var(--bg-surface)',
  fontSize: 13,
};

const tableWrap: CSSProperties = {
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-card)',
  background: 'var(--bg-surface)',
  overflow: 'auto',
};

const th: CSSProperties = {
  textAlign: 'left',
  padding: '10px 12px',
  fontWeight: 600,
  color: 'var(--fg-muted)',
  background: 'var(--bg-sunken)',
  borderBottom: '1px solid var(--border-subtle)',
};
const td: CSSProperties = { padding: '10px 12px', verticalAlign: 'middle' };
const empty: CSSProperties = { padding: 40, textAlign: 'center' };

// 좁힘 배너 — 인프라 발주 목록(pages/InfrastructurePage 의 filterBanner)과 같은 모양.
// 두 화면이 각자 이 스타일을 들고 있다: 공용 컴포넌트로 합치려면 인프라 발주 페이지도
// 같이 고쳐야 하는데 이번 차수의 담당 범위가 아니다(후속으로 남긴다).
const filterBanner: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  padding: '10px 14px',
  background: 'var(--brand-subtle)',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--radius-md)',
  fontSize: 13,
  color: 'var(--fg-default)',
};

const newBtn: CSSProperties = {
  marginLeft: 8,
  height: 32,
  padding: '0 14px',
  display: 'inline-flex',
  alignItems: 'center',
  borderRadius: 'var(--r-btn)',
  background: 'var(--brand)',
  color: 'var(--brand-on)',
  fontSize: 13,
  fontWeight: 500,
  textDecoration: 'none',
  boxShadow: 'var(--shadow-brand)',
};
